// PerformanceGrade — Details-tab Performance card content. Composes
// the GradeLetter (large variant) on the left with three PillarDials
// (Deliverables / Milestones / KPIs) on the right.
//
// The grade is derived from the same pillar scores the dials show
// using the shared contractGrade lib that mirrors the backend, so the
// Details surface and the Contract Register grade column never
// disagree against the same data.
//
// Per-contract `performanceWatchThreshold` drives BOTH the dial colour
// for "critical" pillars AND the floor-rule cap on the grade — keeping
// dial reds and grade caps in sync. Org-level `performanceGradeThresholdOffset`
// shifts the A-E band boundaries uniformly.
//
// The "How is this calculated?" drawer is wired by the parent (the
// Performance card header in ContractDetailsTab) so the link can sit
// on the same row as the card title. `gradeFromPerformanceResult` is
// exported so the parent can compute the same grade for the drawer
// without re-implementing the derivation.

import { GradeLetter } from "./GradeLetter";
import { PillarDial } from "./PillarDial";
import {
  gradeFromPerformanceResult,
  pillarToScore,
} from "./performanceGradeHelpers";
import {
  DEFAULT_SUCCESS_THRESHOLD,
  DEFAULT_WATCH_THRESHOLD,
  type ContractPerformanceResult,
} from "../../../lib/contract-overview/contractPerformance";

interface PerformanceGradeProps {
  result: ContractPerformanceResult;
  /** Per-contract success threshold; defaults to 90. */
  successThreshold?: number | null;
  /** Per-contract watch threshold; defaults to 70. Drives dial colour
   *  AND the critical-pillar count for the grade floor rule. */
  watchThreshold?: number | null;
  /** Org-level grade-threshold offset (-30..+10). Defaults to 0. */
  gradeThresholdOffset?: number | null;
}

export function PerformanceGrade({
  result,
  successThreshold,
  watchThreshold,
  gradeThresholdOffset,
}: PerformanceGradeProps) {
  const success = successThreshold ?? DEFAULT_SUCCESS_THRESHOLD;
  const watch = watchThreshold ?? DEFAULT_WATCH_THRESHOLD;
  const offset = gradeThresholdOffset ?? 0;

  const grade = gradeFromPerformanceResult(result, watch, offset);

  // Layout: stack on narrow viewports; lay out horizontally at md+.
  // Performance card occupies a full-width row on Details so at desktop
  // widths there is room for the GradeLetter on the left and three
  // dials spread across the remaining width.
  return (
    <div
      data-testid="performance-grade"
      className="flex flex-col items-stretch gap-6 md:flex-row md:items-stretch md:gap-10"
    >
      <div className="md:w-44 md:shrink-0">
        <GradeLetter result={grade} variant="large" />
      </div>
      <div
        className="flex items-center justify-around gap-4 md:flex-1 md:justify-around md:gap-8"
        data-testid="performance-grade-dials"
      >
        <PillarDial
          label="Deliverables"
          subLabel="on time"
          score={pillarToScore(result.deliverables)}
          sampleSize={result.deliverables.sampleSize}
          minSample={1}
          successThreshold={success}
          watchThreshold={watch}
          testId="deliverables"
        />
        <PillarDial
          label="Milestones"
          subLabel="on time"
          score={pillarToScore(result.milestones)}
          sampleSize={result.milestones.sampleSize}
          minSample={2}
          successThreshold={success}
          watchThreshold={watch}
          testId="milestones"
        />
        <PillarDial
          label="KPIs"
          subLabel="on track"
          score={pillarToScore(result.kpis)}
          sampleSize={result.kpis.sampleSize}
          minSample={2}
          successThreshold={success}
          watchThreshold={watch}
          testId="kpis"
        />
      </div>
    </div>
  );
}

export default PerformanceGrade;
